import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

const OTPInput = ({ length = 6, onComplete }) => {
  const [values, setValues] = useState(Array(length).fill(''));
  const refs = useRef([]);

  const handleChange = (val, idx) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...values];
    next[idx] = val;
    setValues(next);
    if (val && idx < length - 1) refs.current[idx + 1]?.focus();
    if (next.every(v => v !== '')) onComplete(next.join(''));
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !values[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pasted.length === length) {
      setValues(pasted.split(''));
      refs.current[length - 1]?.focus();
      onComplete(pasted);
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-container">
      {values.map((v, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={v}
          onChange={e => handleChange(e.target.value, i)}
          onKeyDown={e => handleKeyDown(e, i)}
          onPaste={handlePaste}
          data-testid={`otp-digit-${i}`}
          className="w-12 h-14 text-center text-xl font-bold border border-border rounded-lg bg-background focus:border-[hsl(16,100%,55%)] focus:ring-2 focus:ring-[hsl(16,100%,55%)]/20 outline-none transition-all"
        />
      ))}
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa' | 'backup'
  const [tempToken, setTempToken] = useState(null);
  const [backupCode, setBackupCode] = useState('');
  const { login, complete2FA, complete2FABackup } = useAuth();
  const navigate = useNavigate();

  const navigateByRole = (user) => {
    if (user.role === 'superadmin') { navigate('/superadmin'); return; }
    if (user.role === 'sales') { navigate('/sales'); return; }
    if (user.role !== 'admin') { toast.error('Du har inte administratörsrättigheter.'); return; }
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setTempToken(result.tempToken);
        setStep('2fa');
        toast.info('Ange din verifieringskod');
      } else {
        toast.success('Inloggning lyckades!');
        navigateByRole(result.user);
      }
    } catch (error) {
      toast.error('Inloggning misslyckades. Kontrollera dina uppgifter.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPComplete = async (code) => {
    setLoading(true);
    try {
      const user = await complete2FA(tempToken, code);
      toast.success('Inloggning lyckades!');
      navigateByRole(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ogiltig verifieringskod');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await complete2FABackup(tempToken, backupCode);
      toast.success('Inloggning lyckades!');
      navigateByRole(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ogiltig reservkod');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left — Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-black">
        <img
          src="https://images.unsplash.com/photo-1721995019477-99729ca02594?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsJTIwYXJjaGl0ZWN0dXJlJTIwY29uc3RydWN0aW9uJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc0ODY4MDMwfDA&ixlib=rb-4.1.0&q=85"
          alt="Modern architecture"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <img src="/operio-logo-2.png" alt="Operio" className="w-16 h-16 mb-4" />
          <h1 className="text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Operio
          </h1>
          <p className="text-white/70 mt-2 text-base">
            Digital administration för entreprenadföretag
          </p>
        </div>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex items-center justify-center px-6 bg-background">
        <div className="w-full max-w-sm">

          {/* Step: Credentials */}
          {step === 'credentials' && (
            <>
              <div className="mb-10">
                <h2 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Logga in
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">Administrationspanelen</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">E-post</Label>
                  <Input id="email" type="email" placeholder="din@epost.se" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Lösenord</Label>
                    <Link to="/glomt-losenord" className="text-xs text-[hsl(16,100%,55%)] hover:underline" data-testid="forgot-password-link">Glömt?</Link>
                  </div>
                  <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="h-11" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-11 bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white font-medium">
                  {loading ? 'Loggar in...' : 'Logga in'}
                </Button>
              </form>
              <div className="mt-8 space-y-3 text-center">
                <p className="text-sm text-muted-foreground">
                  Inget konto?{' '}
                  <Link to="/register" className="text-[hsl(16,100%,55%)] hover:underline font-medium" data-testid="goto-register-link">Registrera</Link>
                </p>
                <p className="text-sm text-muted-foreground">
                  Personal?{' '}
                  <Link to="/worker/login" className="text-[hsl(16,100%,55%)] hover:underline font-medium" data-testid="goto-worker-login-link">Logga in här</Link>
                </p>
              </div>
            </>
          )}

          {/* Step: 2FA Code */}
          {step === '2fa' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[hsl(16,100%,55%)]/10 mb-6">
                <ShieldCheck size={32} className="text-[hsl(16,100%,55%)]" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Tvåstegsverifiering
              </h2>
              <p className="text-muted-foreground text-sm mb-8">
                Ange 6-siffrig kod från din autentiseringsapp
              </p>
              <OTPInput length={6} onComplete={handleOTPComplete} />
              {loading && <p className="text-sm text-muted-foreground mt-4">Verifierar...</p>}
              <div className="mt-8 space-y-3">
                <button onClick={() => setStep('backup')} className="text-sm text-[hsl(16,100%,55%)] hover:underline" data-testid="use-backup-code-btn">
                  Använd reservkod istället
                </button>
                <br />
                <button onClick={() => { setStep('credentials'); setTempToken(null); }} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-login-btn">
                  <ArrowLeft size={14} /> Tillbaka
                </button>
              </div>
            </div>
          )}

          {/* Step: Backup Code */}
          {step === 'backup' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[hsl(16,100%,55%)]/10 mb-6">
                <ShieldCheck size={32} className="text-[hsl(16,100%,55%)]" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Reservkod
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                Ange en av dina reservkoder
              </p>
              <form onSubmit={handleBackupSubmit} className="space-y-4">
                <Input
                  type="text"
                  placeholder="Ange 8-siffrig reservkod"
                  value={backupCode}
                  onChange={e => setBackupCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={8}
                  data-testid="backup-code-input"
                  className="h-11 text-center text-lg tracking-widest"
                />
                <Button type="submit" disabled={loading || backupCode.length < 8} data-testid="verify-backup-btn" className="w-full h-11 bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white font-medium">
                  {loading ? 'Verifierar...' : 'Verifiera'}
                </Button>
              </form>
              <div className="mt-6 space-y-3">
                <button onClick={() => setStep('2fa')} className="text-sm text-[hsl(16,100%,55%)] hover:underline" data-testid="use-totp-btn">
                  Använd autentiseringsapp istället
                </button>
                <br />
                <button onClick={() => { setStep('credentials'); setTempToken(null); }} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                  <ArrowLeft size={14} /> Tillbaka
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
